import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Bot,
  CheckCheck,
  ChevronRight,
  History,
  Loader2,
  Plus,
  Send,
} from 'lucide-react';
import { ActionProposalCard } from '@/components/chat/ActionProposalCard';
import {
  useChatMessages,
  useSendChatMessage,
} from '@/hooks/useChatMessages';
import {
  useChatThreads,
  useCreateChatThread,
} from '@/hooks/useChatThreads';
import {
  useChatActionHistory,
  useChatAllowlist,
} from '@/hooks/useChatActions';
import { cn } from '@/utils/cn';
import { MarkdownContent } from '@/components/ui/MarkdownContent';
import type { ChatActionProposal, ChatApiError, ChatMessage } from '@/types/chat';
import axios from 'axios';

/** Exact colors from the provided Chat HTML reference */
const C = {
  surface: '#f7f9fb',
  tertiary: '#0c1424',
  onSurface: '#191c1e',
  onSurfaceVariant: '#45474c',
  outlineVariant: '#c5c6cd',
  surfaceLowest: '#ffffff',
  surfaceContainer: '#eceef0',
  surfaceContainerLow: '#f2f4f6',
  surfaceTint: '#545f73',
  secondary: '#3c6187',
  secondaryContainer: '#add2fe',
  userBubble: '#5980A6',
} as const;

const ANALYTICS_PROMPTS = [
  'How many campaigns are enabled?',
  'Top 5 campaigns by spend last week',
  'When did reports last sync?',
  'Which campaigns use hourly budget schedules?',
  'Keywords with high spend and low sales last 7 days',
];

const ACTION_PROMPTS: Record<string, string[]> = {
  campaign_status_bulk: [
    'Pause enabled SP campaigns named containing test (limit 5)',
    'Enable paused SP campaigns named containing demo (limit 5)',
  ],
  campaign_budget_bulk: [
    'Increase daily budget 10% for enabled SP campaigns named containing brand (limit 5)',
  ],
  keyword_bid_bulk: [
    'Cut bids 15% on SP keywords containing brand (limit 10)',
  ],
  keyword_state_bulk: [
    'Pause SP keywords containing test (limit 10)',
  ],
  negative_keyword_add_bulk: [
    'Propose negatives from wasted search terms last 7 days (limit 10)',
  ],
  keyword_add_from_search_terms: [
    'Add top converting search terms last 14 days as exact keywords at bid 12 (limit 10)',
  ],
  recommendation_apply_pack: [
    'Show keyword recommendations and prepare an apply pack (limit 20)',
  ],
  automation_pending_batch: [
    'What automation actions are waiting? Propose confirming them',
  ],
  hourly_budget_schedule_bulk: [
    'Apply evening dayparting 18:00-22:00 at 1.5x to SP campaigns named containing brand (limit 3)',
  ],
  bid_snapshot_capture: [
    'Snapshot current SP keyword bids for this profile',
  ],
  bid_snapshot_restore: [
    'Restore the latest bid snapshot after I review it',
  ],
};

function extractErrorMessage(err: unknown): string {
  if (axios.isAxiosError(err)) {
    const detail = err.response?.data?.detail as ChatApiError | string | undefined;
    if (detail && typeof detail === 'object' && 'message' in detail) {
      return detail.message;
    }
    if (typeof detail === 'string') return detail;
    return err.message;
  }
  if (err instanceof Error) return err.message;
  return 'Something went wrong';
}

function formatRelativeTime(iso: string | null | undefined): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const diffMs = Date.now() - date.getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function formatMessageTime(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

function MessageBubble({
  message,
  profileId,
  threadId,
  onOpenProposal,
}: {
  message: ChatMessage;
  profileId: string;
  threadId: string;
  onOpenProposal?: (p: ChatActionProposal) => void;
}) {
  const isUser = message.role === 'user';
  const proposalId = message.proposal_id ?? null;
  const timeLabel = formatMessageTime(message.created_at);

  if (isUser) {
    return (
      <div className="mx-auto flex w-full max-w-4xl justify-end gap-4">
        <div className="flex flex-col items-end">
          <div
            className="max-w-md rounded-2xl rounded-tr-sm px-4 py-3 text-[14px] leading-5 text-white shadow-sm"
            style={{ backgroundColor: C.userBubble }}
          >
            <p className="whitespace-pre-wrap">{message.content}</p>
          </div>
          {timeLabel ? (
            <div
              className="mt-1 flex items-center gap-1 text-[11px] font-semibold leading-[14px] tracking-wide"
              style={{ color: `${C.onSurfaceVariant}B3` }}
            >
              {timeLabel}
              <CheckCheck className="h-3 w-3" strokeWidth={2.5} aria-hidden />
            </div>
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto flex w-full max-w-4xl gap-4">
      <div
        className="mt-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-white shadow-sm"
        style={{ backgroundColor: C.tertiary }}
        aria-hidden
      >
        <Bot className="h-[18px] w-[18px]" />
      </div>
      <div className="min-w-0 flex-1 space-y-4">
        <div
          className="rounded-lg border p-5 text-[14px] leading-5 shadow-sm sm:p-6"
          style={{
            backgroundColor: C.surfaceLowest,
            borderColor: C.outlineVariant,
            color: C.onSurface,
          }}
        >
          <div style={{ color: C.onSurfaceVariant }}>
            <MarkdownContent
              content={message.content}
              className="[&_li]:leading-relaxed [&_p]:mb-4 [&_p]:leading-relaxed [&_p:last-child]:mb-0 [&_strong]:font-semibold [&_strong]:text-[#191c1e]"
            />
          </div>
        </div>
        {proposalId ? (
          <ActionProposalCard
            profileId={profileId}
            threadId={threadId}
            proposalId={proposalId}
            onOpenProposal={onOpenProposal}
          />
        ) : null}
      </div>
    </div>
  );
}

export function ChatPage() {
  const { profileId: profileIdParam } = useParams<{ profileId: string }>();
  const profileId = profileIdParam ? decodeURIComponent(profileIdParam) : '';
  const [activeThreadId, setActiveThreadId] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [showHistory, setShowHistory] = useState(false);
  const [overlayProposal, setOverlayProposal] = useState<ChatActionProposal | null>(null);
  const messagesScrollRef = useRef<HTMLDivElement>(null);

  const { data: threads = [], isLoading: threadsLoading } = useChatThreads(profileId);
  const createThread = useCreateChatThread(profileId);
  const { data: messages = [], isLoading: messagesLoading } = useChatMessages(
    profileId,
    activeThreadId
  );
  const sendMessage = useSendChatMessage(profileId);
  const { data: allowlist } = useChatAllowlist(profileId);
  const { data: history = [], isLoading: historyLoading } = useChatActionHistory(
    profileId,
    showHistory
  );

  const suggestedPrompts = useMemo(() => {
    const actionChips: string[] = [];
    if (allowlist?.enabled) {
      for (const t of allowlist.action_types ?? []) {
        actionChips.push(...(ACTION_PROMPTS[t] ?? []));
      }
    }
    return [...actionChips, ...ANALYTICS_PROMPTS];
  }, [allowlist]);

  useEffect(() => {
    if (!activeThreadId && threads.length > 0) {
      setActiveThreadId(threads[0].id);
    }
  }, [threads, activeThreadId]);

  useEffect(() => {
    const scroller = messagesScrollRef.current;
    if (!scroller) return;
    scroller.scrollTop = scroller.scrollHeight;
  }, [messages, sendMessage.isPending, overlayProposal]);

  const handleNewThread = async () => {
    setError(null);
    try {
      const thread = await createThread.mutateAsync('New chat');
      setActiveThreadId(thread.id);
    } catch (err) {
      setError(extractErrorMessage(err));
    }
  };

  const handleSend = async (text?: string) => {
    const content = (text ?? draft).trim();
    if (!content || sendMessage.isPending) return;
    setError(null);

    let threadId = activeThreadId;
    if (!threadId) {
      try {
        const thread = await createThread.mutateAsync('New chat');
        threadId = thread.id;
        setActiveThreadId(thread.id);
      } catch (err) {
        setError(extractErrorMessage(err));
        return;
      }
    }

    setDraft('');
    try {
      await sendMessage.mutateAsync({ content, threadId });
    } catch (err) {
      setError(extractErrorMessage(err));
      setDraft(content);
    }
  };

  const busy = sendMessage.isPending || createThread.isPending;
  const toggleHistory = () => setShowHistory((v) => !v);

  return (
    <div
      className="flex h-full min-h-0 overflow-hidden"
      style={{ backgroundColor: C.surface, color: C.onSurface }}
    >
      {/*
        Left: conversation rail (screenshot) — backend threads / new chat.
        Right: exact HTML Chat UI (header → scroll messages → pinned composer).
        App nav (Workspace / Automations / …) lives in AppShell — not modified.
      */}
      <aside
        className="flex h-full w-[280px] shrink-0 flex-col border-r"
        style={{
          backgroundColor: C.surfaceLowest,
          borderColor: C.outlineVariant,
        }}
      >
        <div className="p-3 pb-2">
          <button
            type="button"
            onClick={() => void handleNewThread()}
            disabled={busy}
            className="flex h-9 w-full items-center justify-center gap-2 rounded-lg bg-brand-ink text-[13px] font-medium text-white disabled:opacity-50"
          >
            <Plus className="h-4 w-4" />
            New chat
          </button>
        </div>

        <div className="px-3 pb-2">
          <p
            className="px-1 text-[11px] font-semibold uppercase tracking-[0.05em]"
            style={{ color: C.onSurfaceVariant }}
          >
            Conversations
          </p>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-2 pb-2">
          {threadsLoading ? (
            <p className="px-2 text-[12px]" style={{ color: C.onSurfaceVariant }}>
              Loading…
            </p>
          ) : threads.length === 0 ? (
            <p className="px-2 text-[12px]" style={{ color: C.onSurfaceVariant }}>
              No conversations yet
            </p>
          ) : (
            <ul className="space-y-0.5">
              {threads.map((t) => {
                const active = activeThreadId === t.id;
                const relative = formatRelativeTime(t.updated_at ?? t.created_at);
                return (
                  <li key={t.id}>
                    <button
                      type="button"
                      onClick={() => setActiveThreadId(t.id)}
                      className="w-full rounded-lg px-3 py-2.5 text-left transition-colors"
                      style={{
                        backgroundColor: active
                          ? `${C.secondaryContainer}80`
                          : 'transparent',
                      }}
                      onMouseEnter={(e) => {
                        if (!active) {
                          e.currentTarget.style.backgroundColor = C.surfaceContainerLow;
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!active) {
                          e.currentTarget.style.backgroundColor = 'transparent';
                        }
                      }}
                    >
                      <span
                        className={cn(
                          'block truncate text-[12px]',
                          active ? 'font-semibold' : 'font-medium'
                        )}
                        style={{ color: active ? C.tertiary : C.onSurface }}
                      >
                        {t.title || 'Untitled'}
                      </span>
                      {relative ? (
                        <span
                          className="mt-0.5 block text-[10px]"
                          style={{ color: C.onSurfaceVariant }}
                        >
                          {relative}
                        </span>
                      ) : null}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div
          className="mt-auto shrink-0 border-t px-3 py-3"
          style={{ borderColor: C.outlineVariant }}
        >
          <button
            type="button"
            onClick={toggleHistory}
            className="flex h-14 w-full items-center justify-between gap-2 rounded-lg border px-3 text-left text-[12px] font-medium transition-colors"
            style={{
              borderColor: showHistory ? C.secondary : C.outlineVariant,
              backgroundColor: showHistory
                ? `${C.secondaryContainer}66`
                : C.surfaceLowest,
              color: C.onSurface,
            }}
          >
            <span className="inline-flex items-center gap-2">
              <History className="h-3.5 w-3.5" style={{ color: C.onSurfaceVariant }} />
              Action History
            </span>
            <ChevronRight className="h-3.5 w-3.5" style={{ color: C.onSurfaceVariant }} />
          </button>
        </div>
      </aside>

      {/* Exact HTML main column */}
      <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
        <div
          className="flex shrink-0 items-center px-8 py-4"
          style={{
            backgroundColor: C.surface,
            borderBottom: `1px solid ${C.outlineVariant}4D`,
          }}
        >
          <div className="min-w-0">
            <h2
              className="text-[24px] font-bold leading-8"
              style={{ color: C.onSurface }}
            >
              Chat
            </h2>
            <p className="text-[14px] leading-5" style={{ color: C.onSurfaceVariant }}>
              Ask questions or propose campaign changes for review
            </p>
          </div>
        </div>

        <div
          ref={messagesScrollRef}
          className="flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto scroll-smooth px-8 py-6"
        >
          {messagesLoading && activeThreadId ? (
            <div className="flex flex-1 items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin" style={{ color: C.secondary }} />
            </div>
          ) : messages.length === 0 ? (
            <div className="mx-auto flex w-full max-w-4xl flex-1 flex-col justify-center">
              <div className="w-full max-w-2xl">
                <h3
                  className="mb-4 text-[20px] font-semibold leading-7"
                  style={{ color: C.onSurface }}
                >
                  Suggested questions
                </h3>
                <div className="flex flex-wrap gap-2">
                  {suggestedPrompts.map((prompt) => (
                    <button
                      key={prompt}
                      type="button"
                      onClick={() => void handleSend(prompt)}
                      disabled={busy}
                      className="rounded-full border px-3 py-1.5 text-left text-[12px] transition-colors disabled:opacity-50"
                      style={{
                        borderColor: C.outlineVariant,
                        backgroundColor: C.surfaceContainer,
                        color: C.onSurfaceVariant,
                      }}
                      onMouseEnter={(e) => {
                        if (!busy) {
                          e.currentTarget.style.backgroundColor = C.surfaceLowest;
                          e.currentTarget.style.color = C.onSurface;
                        }
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = C.surfaceContainer;
                        e.currentTarget.style.color = C.onSurfaceVariant;
                      }}
                    >
                      {prompt}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <>
              {messages.map((m) =>
                activeThreadId ? (
                  <MessageBubble
                    key={m.id}
                    message={m}
                    profileId={profileId}
                    threadId={activeThreadId}
                    onOpenProposal={setOverlayProposal}
                  />
                ) : null
              )}
              {overlayProposal ? (
                <div className="mx-auto w-full max-w-4xl pl-12">
                  <ActionProposalCard
                    profileId={profileId}
                    threadId={overlayProposal.thread_id}
                    proposalId={overlayProposal.id}
                  />
                </div>
              ) : null}
              {sendMessage.isPending ? (
                <div
                  className="mx-auto flex w-full max-w-4xl items-center gap-2 pl-12 text-[14px]"
                  style={{ color: C.onSurfaceVariant }}
                >
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Working on your request…
                </div>
              ) : null}
              <div className="h-8 shrink-0" aria-hidden />
            </>
          )}
        </div>

        {error ? (
          <div className="shrink-0 px-8 pb-2">
            <div className="mx-auto max-w-4xl rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-[14px] text-red-800">
              {error}
            </div>
          </div>
        ) : null}

        <div
          className="shrink-0 border-t p-4 shadow-[0_-4px_6px_-1px_color-mix(in_srgb,var(--brand-ink)_2%,transparent)]"
          style={{
            backgroundColor: C.surface,
            borderColor: `${C.outlineVariant}4D`,
          }}
        >
          <div className="relative mx-auto max-w-4xl">
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  void handleSend();
                }
              }}
              placeholder="Ask about data or propose campaign status/budget changes..."
              rows={1}
              disabled={busy}
              className="h-[52px] max-h-32 w-full resize-none rounded-xl border py-3 pl-4 pr-12 text-[14px] leading-5 shadow-sm placeholder:opacity-50 focus:outline-none disabled:opacity-50"
              style={{
                backgroundColor: C.surfaceLowest,
                borderColor: C.outlineVariant,
                color: C.onSurface,
              }}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = C.secondary;
                e.currentTarget.style.boxShadow = `0 0 0 1px ${C.secondary}`;
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = C.outlineVariant;
                e.currentTarget.style.boxShadow = 'none';
              }}
            />
            <button
              type="button"
              onClick={() => void handleSend()}
              disabled={busy || !draft.trim()}
              aria-label="Send"
              className="absolute right-2 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-lg text-white shadow-sm transition-colors disabled:cursor-not-allowed disabled:opacity-50"
              style={{ backgroundColor: C.surfaceTint }}
              onMouseEnter={(e) => {
                if (!e.currentTarget.disabled) {
                  e.currentTarget.style.backgroundColor = C.secondary;
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = C.surfaceTint;
              }}
            >
              {busy ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                <Send className="h-5 w-5" />
              )}
            </button>
          </div>
        </div>
      </div>

      {showHistory ? (
        <aside
          className="flex h-full w-72 shrink-0 flex-col gap-2 overflow-hidden border-l p-3"
          style={{
            backgroundColor: C.surfaceLowest,
            borderColor: C.outlineVariant,
          }}
        >
          <p className="px-1 text-[12px] font-semibold" style={{ color: C.onSurface }}>
            Action History
          </p>
          {historyLoading ? (
            <p className="px-1 text-[12px]" style={{ color: C.onSurfaceVariant }}>
              Loading…
            </p>
          ) : history.length === 0 ? (
            <p className="px-1 text-[12px]" style={{ color: C.onSurfaceVariant }}>
              No confirmed actions yet
            </p>
          ) : (
            <ul className="flex-1 space-y-2 overflow-y-auto">
              {history.map((item) => (
                <li key={item.proposal_id}>
                  <button
                    type="button"
                    className="w-full rounded-lg border px-3 py-2 text-left transition-colors"
                    style={{ borderColor: C.outlineVariant }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = C.surfaceContainerLow;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                    onClick={() => {
                      setActiveThreadId(item.thread_id);
                      setOverlayProposal({
                        id: item.proposal_id,
                        thread_id: item.thread_id,
                        message_id: null,
                        profile_id: profileId,
                        action_type: item.action_type,
                        status: item.status as ChatActionProposal['status'],
                        title: item.title,
                        summary: null,
                        params: {},
                        items: [],
                        entity_count: item.entity_count,
                        schema_version: 1,
                        expires_at: null,
                        undo_expires_at: item.undo_expires_at,
                        confirmed_at: item.confirmed_at,
                        parent_proposal_id: item.parent_proposal_id,
                        undo_proposal_id: null,
                        is_undo: item.is_undo,
                        llm_exposed: false,
                        error_reason: null,
                        undo_eligible: item.undo_eligible,
                        created_at: item.created_at,
                        updated_at: null,
                      });
                    }}
                  >
                    <div
                      className="truncate text-[12px] font-medium"
                      style={{ color: C.onSurface }}
                    >
                      {item.title}
                    </div>
                    <div
                      className="mt-1 text-[10px]"
                      style={{ color: C.onSurfaceVariant }}
                    >
                      {item.status} · {item.entity_count} entities
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </aside>
      ) : null}
    </div>
  );
}
